/**
 * AtlasBus — a tiny typed emitter for Atlas-internal cross-module signals.
 *
 * The engine `EventBus` is strictly typed to `EngineEventMap`, so P0's
 * app-specific signals live here instead. Fire-and-forget, synchronous.
 */

export interface AtlasEventMap {
  /** The local identity now has an Account (created or resolved). */
  'atlas:account-ready': { identityHex: string; username: string; displayName: string };
  /** A world script logged (world.log) or errored — the Lua IDE console renders these. */
  'atlas:script-log': { script: string; msg: string; level: 'info' | 'error' };
  /** The local avatar entered a world. */
  'atlas:world-entered': { worldId: bigint };
  /** Follow a SERVER-side world hop (enterSpace/exitSpace/shuttle already moved
   *  our playerState) — the client adopts the new world without re-calling
   *  enterWorld (which would reset the authoritative arrival position). */
  'atlas:world-adopted': { worldId: bigint; x: number; z: number };
  /** The boot loading screen finished (all world systems ready or failsafe). */
  'atlas:loading-done': Record<string, never>;
  /** The journey BEGINS (the traveler's BEGIN, or the menu's Enter without a
   *  traveler screen): the INTRO runs first — the narration card, then the
   *  film — and the journey gate (AtlasIntroVideoModule) forwards
   *  atlas:menu-enter only when it ends or is skipped, so nothing of the
   *  world loads under the intro (2026-09-09). lib/journey.ts emits it. */
  'atlas:journey-begin': { worldId: bigint | null };
  /** Main menu: the player chose to enter (null = Commons / last world). */
  'atlas:menu-enter': { worldId: bigint | null };
  /** A world was picked in the menu dock — the Traveler screen takes over. */
  'atlas:world-picked': { worldId: bigint | null };
  /** The Traveler screen's BACK — the main menu returns. */
  'atlas:traveler-back': Record<string, never>;
  /** The input layer requests placing an object at the local avatar. */
  'atlas:place-requested': Record<string, never>;
  /** An ATTACK request (F, a pointer-locked LEFT click, the touch button): the
   *  wilds strike and the bounty hunter's fight-back both listen — ONE path,
   *  never a synthetic key event (combat wave, 2026-09-05). */
  'atlas:attack': { source: 'key' | 'mouse' | 'touch' | 'gamepad' };
  /** One scroll-wheel step through the weapon ring while the pointer is locked
   *  (+1 = next, −1 = previous); the combat HUD swaps. */
  'atlas:weapon-cycle': { dir: 1 | -1 };
  /** SOFT TARGET (build-now wave 5): the mark the next attack would go to —
   *  the same cone-and-reach pick the strike uses, refreshed ~10 Hz by the
   *  wilds module and drawn by the combat HUD; null = nothing in the cone. */
  'atlas:soft-target': { x: number; y: number; label: string; kind: string; id: string; blocked?: boolean; committed?: boolean } | null;
  /** Ground PvP wave (2026-09-18): a chat slash command for the PvP module
   *  (/duel, /accept, /decline, /ready, /yield, /arena, /enroll, /disengage …). */
  'atlas:pvp-cmd': { cmd: string; args: string[] };
  /** Player-to-player wave (2026-09-18): a chat slash command for the Social module
   *  (/party /invite /accept /friend /block /mute /report /trade /ping /p …). */
  'atlas:social-cmd': { cmd: string; args: string[] };
  /** A line for the chat panel from another module (a private whisper arriving through row-level security). */
  'atlas:chat-line': { who: string; text: string; color: string };
  /** A committed blow is coming at ME (a combat_action row with a startup): the tell. */
  'atlas:pvp-tell': { attackerHex: string; itemKey: string; impactInMs: number } | null;
  /** A shot landed here (absolute metres) — nearby heads turn (attention priority, 2026-09-15). */
  'atlas:impact': { x: number; y: number; z: number };
  /** The input layer requests toggling between the Commons and the personal world. */
  'atlas:toggle-world': Record<string, never>;
  /** Open or close the Options (Esc) menu. The gamepad's Start button emits it:
   *  the menu is otherwise keyboard-only, and this codebase never synthesizes a
   *  KeyboardEvent to reach a handler. */
  'atlas:toggle-options': Record<string, never>;
  /** Connection status changed (for the HUD). */
  'atlas:status': { text: string };
  /** External damage to the player's FLYING ship (war-air return fire) — AtlasVehicleModule applies it only while in flight. */
  'atlas:ship-damage': { amount: number; why: string };
  /** The bounty hunt's EVIDENCE level moved (0 ambient … 3 confirmed) — the space contact's
   *  resolution and the ground module both raise it; the HUD line follows it. */
  'atlas:bounty-evidence': { level: number; why: string };
  /** Step the local player off a ridden hovercraft NOW (the bus is synchronous:
   *  `atlas.hoverRide` is false when emit returns). The sky-ship lift needs
   *  the avatar back on its own capsule BEFORE it lifts (the ride handoff). */
  'atlas:vehicle-dismount': { why: string };
  /** Reconcile hold while an authoritative relocation (the sky-ship lift) is
   *  in flight: 'hold' raises the grace to at least `ticks`, 'settle' lowers
   *  it to at most `ticks` (AtlasAvatarModule — the unstick-nudge pattern). */
  'atlas:reconcile-hold': { ticks: number; mode: 'hold' | 'settle' };
  /** A placed object arrived in the current world. */
  'atlas:object-added': {
    objectId: bigint;
    prefabId: string;
    x: number;
    y: number;
    z: number;
    rotX: number;
    rotY: number;
    rotZ: number;
    rotW: number;
    scale: number;
  };
  /** A placed object was removed from the current world. */
  'atlas:object-removed': { objectId: bigint };
  /** The current world changed — clear all world-scoped meshes (objects + remotes). */
  'atlas:world-cleared': Record<string, never>;
  /** A mid-game world SWAP is starting (not boot): the transition loader shows
   *  a cover, holds the avatar, despawns the old area and waits for the new. */
  'atlas:world-swapping': { worldId: bigint; fromSpace: boolean };
  /** An avatar appearance changed (self or a remote) — rebuild its render proxy. */
  'atlas:avatar-updated': { identityHex: string; isLocal: boolean };
  /** The display name (or its cooldown ledger) changed. */
  'atlas:displayname-updated': { displayName: string; lastChangedMicros: bigint };
  /** The local inventory or the global asset graph changed — refresh inventory UI. */
  'atlas:inventory-updated': Record<string, never>;
  /** The local player's wallet balance changed (SOV/INF, base units). */
  'atlas:wallet-updated': { sov: bigint; inf: bigint };
  /** The set of active marketplace listings changed — refresh the market UI. */
  'atlas:listings-updated': Record<string, never>;
  /** Open the provenance lineage browser for an asset. */
  'atlas:show-lineage': { assetId: bigint };
  /** The local player's Social Pulse (or a venue's satisfier) changed. */
  'atlas:pulse-updated': Record<string, never>;
  /** Some avatar's Social Compass changed (self or a visible stranger). */
  'atlas:compass-updated': Record<string, never>;
  /** An Orbit or its membership changed — refresh the roster + hop suggestion. */
  'atlas:orbit-updated': Record<string, never>;
  /** Request a hop into a specific world (e.g. following an Orbit). */
  'atlas:enter-world': { worldId: bigint };
  /** The reputation graph changed (a score, vouch, review, or betrayal row). */
  'atlas:reputation-updated': Record<string, never>;
  /** An event, attendance, ritual, mute, or NPC-host row changed (P8). */
  'atlas:events-updated': Record<string, never>;
  /** A Memory Book entry changed — refresh the archive (P8). */
  'atlas:memory-updated': Record<string, never>;
  /** A group, membership, signature, challenge, treasury, spend, or fork row changed (P9). */
  'atlas:groups-updated': Record<string, never>;
  /** A Creator-Exchange row changed — subscription, game-pass, rental, tip, royalty, entitlement, cert, or settlement (P10). */
  'atlas:exchange-updated': Record<string, never>;
  /** A live-ops row changed — season, Chronicler event, council term/vote/seat/action, or Legacy grant (P12). */
  'atlas:liveops-updated': Record<string, never>;
  /** Onboarding progress, a new Atlas-guide message, or a Beginner-Mixer suggestion changed (P13). */
  'atlas:onboarding-updated': Record<string, never>;
  /** A lot budget, deed, deployed structure, decoration, or no-build zone changed (P14.1). */
  'atlas:structures-updated': Record<string, never>;
  /** Enterable buildings registered terrain-flattening rects — rebuild the touched chunks. */
  'atlas:terrain-restamp': { stamps: Array<{ x: number; z: number; hw: number; hd: number; yaw: number; h: number }> };
  /** The current world's terrain_edit rows changed (Edit Mode sculpt/paint) — rebuild touched ground. */
  'atlas:terrain-edits-changed': { stamps: Array<{ x: number; z: number; r: number }> };
  /** A city, citizen, civic structure, zoning grant, or mayoral election changed (P14.2). */
  'atlas:cities-updated': Record<string, never>;
  /** A terrain region, sky state, or Versona changed (P14.3) — rebuild terrain/sky/NPCs. */
  'atlas:environment-updated': Record<string, never>;
  /** A part, linkset, Land-Impact budget, or part-script changed (P14.4). */
  'atlas:freeform-updated': Record<string, never>;
  'atlas:build-history-changed': Record<string, never>;
  'atlas:structure-place': { deedId: string; structureType: string };
  /** IDE ▶ Run — load a buffer into a LOCAL-ONLY preview slot (never persisted). `npcId` previews an NPC creator script bound to that unit. */
  'atlas:script-preview': { name: string; source: string; npcId?: string; events?: Array<{ at: number; hook: string; args: unknown[] }> };
  /** The preview slot stopped — what ran: hook call counts, errors, seconds (the copilot's test report). */
  'atlas:script-preview-stats': { name: string; hooks: Record<string, number>; errors: number; seconds: number; npcId?: string };
  /** Edit Mode → open the Lua IDE on a script (world script by name, or an NPC unit's script) without leaving Edit Mode. */
  'atlas:ide-open': { kind: 'world'; name: string; source?: string } | { kind: 'npc'; npcId: string; name: string; source?: string };
  /** Edit Mode exited — the IDE (Edit-Mode-only) closes with it. */
  'atlas:ide-close': Record<string, never>;
  /** The world SIGNAL bus: devices, NPC scripts and world scripts talk over named topics (local to this client). */
  'atlas:signal': { topic: string; payload: string; from: string };
  /** A cinematic camera path started/stopped (HUD + input lock follow). */
  'atlas:cinematic': { playing: boolean; name: string };
  /** IDE ■ Stop — unload the preview slot. */
  'atlas:script-preview-stop': Record<string, never>;
  /** A Forge project, scene, instance, or script changed (F1). */
  'atlas:forge-updated': Record<string, never>;
  /** The active graphics quality tier changed (G5) — rescale LOD/shadows/culling/budget. */
  'atlas:quality-changed': Record<string, never>;
  /** A creator upload or its moderation row changed (G6) — refresh the upload panel. */
  'atlas:upload-updated': Record<string, never>;
  /** Open the Help & Manual window (the welcome popup's button, any module). */
  'atlas:open-help': Record<string, never>;
  /** Society wave: open the federal docket window (the menu bar's Assembly entry; never a synthesized key). */
  'atlas:open-federal': Record<string, never>;
  /** Open the Crafting Guide (optionally scrolled to one schematic). */
  'atlas:open-craft-guide': { key?: string };
  /** Open the Workbench on a tab (optionally with a schematic pre-selected). */
  'atlas:open-workbench': { tab?: 'survey' | 'inventory' | 'craft' | 'rigs' | 'market'; key?: string };
}

type Handler<T> = (data: T) => void;

/**
 * STICKY topics: durable-state milestones (not transient signals). A late
 * subscriber immediately receives the last-emitted payload. WHY: the
 * two-phase homepage boots ~60 world modules AFTER atlas:account-ready has
 * fired (it unlocks the menu in phase 1) — 38 modules gate their
 * subscriptions/UI on that event and every phase-2 one silently missed it
 * (first symptom: zero structures anywhere — StructureRenderModule's table
 * subscription lived behind the listener; 2026-08-04). Phase-1 subscribers
 * registered before the emit are unaffected (no double delivery).
 */
// atlas:menu-enter joined 2026-08-16: the traveler BEGIN emits it while the
// phase-2 boot (~60 modules) may still be initializing — AtlasLoadingModule
// subscribed AFTER the emit and its poll clock never started, so the honest
// loading gate could hold FOREVER (no polls ⇒ no stall/hard timeout either).
// Human pacing usually hides the race; fast clicks (probes, keyboard users)
// hit it.
const STICKY = new Set<keyof AtlasEventMap>(['atlas:account-ready', 'atlas:menu-enter']);

class AtlasBus {
  private handlers = new Map<string, Handler<unknown>[]>();
  private sticky = new Map<string, unknown>();

  on<K extends keyof AtlasEventMap>(event: K, handler: Handler<AtlasEventMap[K]>): () => void {
    let list = this.handlers.get(event);
    if (!list) {
      list = [];
      this.handlers.set(event, list);
    }
    list.push(handler as Handler<unknown>);
    if (STICKY.has(event) && this.sticky.has(event)) {
      try {
        handler(this.sticky.get(event) as AtlasEventMap[K]);
      } catch (err) {
        console.error(`[AtlasBus] sticky replay error on ${event}:`, err);
      }
    }
    return () => {
      const arr = this.handlers.get(event);
      if (arr) {
        const i = arr.indexOf(handler as Handler<unknown>);
        if (i !== -1) arr.splice(i, 1);
      }
    };
  }

  /** How many handlers listen on `event` (the journey gate's fallback: a page
   *  without the intro module enters directly — lib/journey.ts). */
  listenerCount<K extends keyof AtlasEventMap>(event: K): number {
    return this.handlers.get(event)?.length ?? 0;
  }

  emit<K extends keyof AtlasEventMap>(event: K, data: AtlasEventMap[K]): void {
    if (STICKY.has(event)) this.sticky.set(event, data);
    const list = this.handlers.get(event);
    if (!list) return;
    for (const h of list.slice()) {
      try {
        h(data);
      } catch (err) {
        console.error(`[AtlasBus] handler error on ${event}:`, err);
      }
    }
  }
}

export const bus = new AtlasBus();
